/**
 * 견적서·거래명세서 계산 규칙. 전부 순수 함수 — 서버도 로그인도 없다.
 *
 * **역할은 수익 라인이 아니라 유입 엔진이다**
 * ([목표 문서 §4-b](../../../../docs/goals/2027-06-30-revenue-10m-krw.md)). 현장 사업자가
 * 실제로 검색하는 실무 문서(`견적서 양식`)를 무료로 즉시 만들어 주고, **사진·영수증으로
 * 자동 작성하는 것**만 유료(L1 구독)로 남긴다. 무료가 먼저 값을 줘야 검색에서 살아남는다.
 *
 * 금액은 **원 단위 정수**로만 다룬다 — 부가세 계산에 부동소수를 쓰면 합계가 1원씩 어긋나고,
 * 그건 거래처에 보내는 문서에서 신뢰를 깎는다.
 */

/** 견적 한 줄 */
export interface QuoteItem {
  /** 품목명 */
  name: string
  /** 수량 — 0 이하는 줄을 없는 것으로 본다 */
  quantity: number
  /** 단가(원, 부가세 별도 기준) */
  unitPrice: number
}

export interface QuoteTotals {
  /** 공급가액 합계 */
  supply: number
  /** 부가세 */
  vat: number
  /** 합계 (공급가액 + 부가세) */
  total: number
}

/** 부가세 처리 방식 — 현장에서 실제로 쓰는 세 가지 */
export type VatMode =
  /** 단가가 부가세 별도 → 10% 를 더한다 */
  | 'exclusive'
  /** 단가에 부가세가 이미 포함 → 합계에서 역산한다 */
  | 'inclusive'
  /** 면세·간이과세 등 부가세 없음 */
  | 'none'

/** 한 줄의 공급가액. 수량·단가가 비정상이면 0 */
export function lineAmount(item: QuoteItem): number {
  if (!Number.isFinite(item.quantity) || !Number.isFinite(item.unitPrice)) return 0
  if (item.quantity <= 0 || item.unitPrice <= 0) return 0
  return Math.round(item.quantity * item.unitPrice)
}

/**
 * 합계를 낸다.
 *
 * **`inclusive` 는 역산이다** — 단가에 부가세가 들어 있으므로 공급가액 = 합계 ÷ 1.1 이고,
 * 부가세는 그 차액이다. 이렇게 해야 **합계가 사용자가 입력한 값과 정확히 같다.**
 * 공급가액을 반올림한 뒤 10% 를 다시 더하면 원래 합계와 1원 어긋날 수 있다.
 */
export function computeTotals(items: readonly QuoteItem[], vat: VatMode): QuoteTotals {
  const gross = items.reduce((sum, item) => sum + lineAmount(item), 0)

  if (vat === 'none') return { supply: gross, vat: 0, total: gross }

  if (vat === 'inclusive') {
    const supply = Math.round(gross / 1.1)
    return { supply, vat: gross - supply, total: gross }
  }

  const vatAmount = Math.round(gross * 0.1)
  return { supply: gross, vat: vatAmount, total: gross + vatAmount }
}

/** 금액 표기 — 문서에 찍히는 값이라 통화 기호 없이 천 단위만 끊는다 */
export function formatWon(amount: number): string {
  return amount.toLocaleString('ko-KR')
}

/**
 * 견적 유효기간. 관행은 발행일로부터 30일이고, 이 값이 없으면 상대가 언제까지 유효한지
 * 묻는 연락이 한 번 더 오간다.
 */
export function validUntil(issuedOn: string, days = 30): string {
  // **UTC 로 고정한다** — 로컬 시간대로 파싱하고 `toISOString()` 으로 뽑으면 KST(+9)에서
  // 하루가 밀린다 (resignationRules.ts 가 같은 이유로 UTC 산술을 쓴다)
  const date = new Date(`${issuedOn}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return ''
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

/** 빈 줄을 뺀 실제 품목 */
export function usableItems(items: readonly QuoteItem[]): QuoteItem[] {
  return items.filter((item) => item.name.trim() !== '' && lineAmount(item) > 0)
}

/**
 * 문서 종류. **표 구조가 같은 것만 한 화면에서 낸다** — 품목·수량·단가·합계가 공통이고
 * 제목·용어만 다르다. 작업완료보고서처럼 구조가 다른 문서는 여기 넣지 않는다.
 *
 * URL 을 종류마다 따로 두는 이유는 **검색이 문서 이름으로 일어나기 때문**이다
 * (`견적서 양식`·`거래명세서 양식`). 한 URL 에 토글로 몰면 그 검색어들을 못 잡는다.
 */
export type DocKind = 'quote' | 'statement' | 'receipt'

export interface DocPreset {
  kind: DocKind
  /** 문서 제목 기본값 */
  title: string
  /** 페이지 h1 */
  heading: string
  /** 한 줄 설명 */
  lead: string
  /** 공급받는 자 칸 라벨 — 문서마다 부르는 말이 다르다 */
  counterpartyLabel: string
  /** 유효기간을 쓰는가 — 견적서만 쓴다. 명세서·영수증은 이미 끝난 거래다 */
  showValidUntil: boolean
}

export const DOC_PRESETS: Record<DocKind, DocPreset> = {
  quote: {
    kind: 'quote',
    title: '견적서',
    heading: '견적서 만들기',
    lead: '품목과 금액만 넣으면 부가세까지 계산된 견적서가 바로 나옵니다.',
    counterpartyLabel: '공급받는 자 (거래처)',
    showValidUntil: true,
  },
  statement: {
    kind: 'statement',
    title: '거래명세서',
    heading: '거래명세서 만들기',
    lead: '납품한 품목과 금액을 넣으면 거래명세서가 바로 나옵니다.',
    counterpartyLabel: '공급받는 자 (거래처)',
    showValidUntil: false,
  },
  receipt: {
    kind: 'receipt',
    title: '영수증',
    heading: '간이영수증 만들기',
    lead: '받은 금액과 내역을 넣으면 영수증이 바로 나옵니다.',
    counterpartyLabel: '받는 분',
    showValidUntil: false,
  },
}
